import { useState } from 'react'

import { Box, Button, Icon, PopoverMenu, Text } from '@factorialco/deprecated-design-system'
import {
  BankingTransferFilter,
  DataTableFilter,
  FilterType,
  ProjectionKind,
  useTransfersDataTable,
} from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import useSelectedRows from 'modules/core/components/DataTable/hooks/useSelectedRows'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { Actions } from 'modules/banking/components/PaymentTransfersDataTable/_Actions'
import { DateLabel } from 'modules/banking/components/PaymentsDataTable/_DateLabel'
import { TransferInsights } from 'modules/banking/components/TransferInsights'

import { AccountNumber } from './_AccountNumber'
import { Amount } from './_Amount'
import { Beneficiary } from './_Beneficiary'
import { Requester } from './_Requester'
import { SourceType } from './_SourceType'

const TransfersDataTableContent = ({
  actions,
  EmptyState,
  filter,
  setFilter,
  currency,
  locale,
  onSelectedIds,
  toggleBulkDeleteConfirmModal,
}: {
  actions: () => React.ReactNode
  EmptyState: React.ReactNode
  filter: string
  setFilter: (value: string) => void
  currency: string
  locale: string
  onSelectedIds: (data: number[]) => void
  toggleBulkDeleteConfirmModal?: () => void
}) => {
  const { selectedRows } = useSelectedRows()

  useUnsafeEffect(() => {
    onSelectedIds(Array.from(selectedRows).map((el) => Number(el)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows])

  return (
    <Box gap="s16">
      <DataTable.HeadBar
        disableExport
        search={{
          value: filter,
          onChange: (ev) => setFilter(ev.target.value),
          placeholder: i18n.t('banking.transfers.data_table.search_placeholder'),
        }}
        actions={actions()}
        customPopoverOptions={
          <PopoverMenu.TouchableOption
            title={i18n.t('banking.transfers.actions.remove_outstanding_payment')}
            onClick={toggleBulkDeleteConfirmModal}
            disabled={selectedRows.size === 0}
          />
        }
      />
      <TransferInsights defaultCurrency={currency} locale={locale} />
      <DataTable.Table emptyState={EmptyState} selectable={() => true} autoWidth />
    </Box>
  )
}

export const TransfersDataTable = ({
  onSelectedIds,
  legalEntitiesOptions,
  requestersOptions,
  handleSubmitAddToBulkPayment,
  disabledAddToBulkPayment,
  toggleAddToPaymentModal,
  disabledAddToPaymentModal,
  disabledBulkDelete,
  toggleBulkDeleteConfirmModal,
  locale,
  currency,
}: {
  onSelectedIds: (data: number[]) => void
  legalEntitiesOptions: { value: number; label: string }[]
  requestersOptions: { value: number; label: string }[]
  handleSubmitAddToBulkPayment?: () => void
  disabledAddToBulkPayment?: boolean
  toggleAddToPaymentModal?: () => void
  disabledAddToPaymentModal?: boolean
  disabledBulkDelete?: boolean
  toggleBulkDeleteConfirmModal?: () => void
  locale: string
  currency: string
}) => {
  const [filter, setFilter] = useState('')
  const { fromDueDate, toDueDate, createdAtFrom, createdAtTo } = useSearchParams<{
    fromDueDate?: string
    toDueDate?: string
    createdAtFrom?: string
    createdAtTo?: string
  }>()

  const actions = () => {
    const onClick = handleSubmitAddToBulkPayment || toggleAddToPaymentModal
    const isDisabled = handleSubmitAddToBulkPayment
      ? disabledAddToBulkPayment
      : disabledAddToPaymentModal
    const label = handleSubmitAddToBulkPayment
      ? i18n.t('banking.transfers.actions.add_to_bulk_payment')
      : i18n.t('banking.transfers.actions.pay_in_bulk')

    return onClick ? (
      <Button
        onClick={() => {
          if (handleSubmitAddToBulkPayment)
            track(trackingEvents.accounting.payments.addToBulkPayment)
          onClick()
        }}
        type="primary"
        disabled={isDisabled}
      >
        {label}
      </Button>
    ) : undefined
  }

  const source = useTransfersDataTable(
    [
      {
        identifier: 'beneficiary',
        title: i18n.t('banking.attributes.beneficiary'),
        kind: ProjectionKind.Custom,
        sortBy: 'beneficiaryName',
        template: '{{ beneficiaryName }}',
        render: ({ beneficiaryName }) => {
          return <Beneficiary name={beneficiaryName} />
        },
      },
      {
        identifier: 'requester',
        title: i18n.t('banking.attributes.requester'),
        kind: ProjectionKind.Custom,
        template: '{{ requester.fullName }}',
        render: ({ requester }) => {
          return <Requester requester={requester} />
        },
      },
      {
        identifier: 'type',
        title: i18n.t('banking.attributes.type'),
        kind: ProjectionKind.Custom,
        sortBy: 'sourceType',
        template: '{{ type }}',
        render: ({ sourceType }) => {
          return <SourceType type={sourceType} />
        },
      },
      {
        identifier: 'amount',
        title: i18n.t('banking.attributes.amount'),
        kind: ProjectionKind.Custom,
        sortBy: 'amountCents',
        template: '{{ amountCents }}',
        render: ({ amountCents, currency: amountCurrency }) => {
          return <Amount amountCents={amountCents} currency={amountCurrency} />
        },
      },
      {
        identifier: 'dueDate',
        title: i18n.t('banking.attributes.due_date'),
        kind: ProjectionKind.Custom,
        sortBy: 'dueDate',
        template: '{{ dueDate }}',
        render: ({ dueDate }) => {
          return <DateLabel date={dueDate} />
        },
      },
      {
        identifier: 'iban',
        title: i18n.t('banking.attributes.iban'),
        kind: ProjectionKind.Custom,
        sortBy: 'accountNumber',
        template: '{{ iban }}',
        render: ({ accountNumber }) => {
          return <AccountNumber accountNumber={accountNumber} />
        },
      },
      {
        kind: ProjectionKind.Custom,
        identifier: 'sourceId',
        title: '',
        template: "{{ sourceId + '-' + sourceType }}",
        render: ({ sourceId, sourceType }) => {
          const expensableId = sourceType === 'expensable' ? sourceId : undefined
          const financialDocumentId = sourceType === 'invoice' ? sourceId : undefined
          return (
            <Actions
              revocable={false}
              expensableId={expensableId}
              financialDocumentId={financialDocumentId}
            />
          )
        },
      },
    ],
    {
      variables: {
        onlyPending: true,
        search: filter,
      },
    }
  )
  const filters: DataTableFilter<BankingTransferFilter>[] = [
    {
      identifier: 'created_at',
      title: i18n.t('banking.transfers.data_table.filters.date'),
      path: 'createdAt',
      default: !!createdAtFrom && !!createdAtTo,
      type: FilterType.Date,
      categories: [
        {
          kind: 'DatePicker',
          operation: 'between',
          value: !!createdAtFrom && !!createdAtTo ? [createdAtFrom, createdAtTo] : undefined,
        },
      ],
    },
    {
      identifier: 'due_date',
      title: i18n.t('banking.transfers.data_table.filters.due_date'),
      path: 'dueDate',
      default: !!toDueDate && !!fromDueDate,
      type: FilterType.Date,
      categories: [
        {
          kind: 'DatePicker',
          operation: 'between',
          value: !!toDueDate && !!fromDueDate ? [fromDueDate, toDueDate] : undefined,
        },
      ],
    },
    {
      identifier: 'legal_entity_id',
      title: i18n.t('banking.transfers.data_table.filters.legal_entity'),
      path: 'legalEntityId',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: legalEntitiesOptions,
        },
      ],
    },
    {
      identifier: 'type',
      title: i18n.t('banking.transfers.data_table.filters.type'),
      path: 'sourceType',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              value: 'Finance::Invoice',
              label: i18n.t('banking.transfers.source_types.invoices'),
            },
            {
              value: 'Expenses::Expensable',
              label: i18n.t('banking.transfers.source_types.expensables'),
            },
          ],
        },
      ],
    },
    {
      identifier: 'requester',
      title: i18n.t('banking.transfers.data_table.filters.requester'),
      path: 'requesterId',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: requestersOptions,
        },
      ],
    },
  ]

  const EmptyState = (
    <>
      <Icon.Large icon="document-folder-drawer-2" color="grey600" />
      <Box flexDirection="column" gap="s4" alignItems="center">
        <Text.Header2>{i18n.t('banking.transfers.data_table.empty_state.title')}</Text.Header2>
      </Box>
    </>
  )

  return (
    <DataTable.Provider
      source={source}
      persistFilters={false}
      pagination={{
        kind: 'offset',
        itemsPerPage: 25,
      }}
      sort={{ path: 'created_at', direction: 'desc' }}
      filters={filters}
    >
      <TransfersDataTableContent
        toggleBulkDeleteConfirmModal={toggleBulkDeleteConfirmModal}
        actions={actions}
        EmptyState={EmptyState}
        filter={filter}
        setFilter={setFilter}
        currency={currency}
        locale={locale}
        onSelectedIds={onSelectedIds}
      />
    </DataTable.Provider>
  )
}
